import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    XCircle, Star, FileText, Paperclip, Eye, Download, Clock, CheckCircle, X, Loader2
} from "lucide-react";
import { MilestoneStatusEnum, ContractStatusEnum, MilestonePaymentStatusEnum, RefundRequestStatusEnum } from "../../modules/shared/model";
import {
    getContractStatusText,
    getContractStatusColor,
    getMilestoneStatusText,
    getMilestoneStatusColor,
    getPaymentStatusText,
    getPaymentStatusColor,
    canAcceptDeclineContract,
    requiresTrackerCheckbox,
    defaultHandleRequestPaymentRelease,
    defaultHandleContractAction,
    defaultHandleTrackerCheckboxChange
} from "../../utils/contractUtils";
import { GlobalService } from "../../modules";
import { useDispatch, useSelector } from "react-redux";
import { updateContract } from "../../redux/Slices/contractSlice";
import { updateMilestone } from "../../redux/Slices/milestoneSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { escrowAPI } from "../../services/escrowAPI";
import { createChat, createConversation, fetchParticipantChats, updateChat } from "../../redux";
import { ContextEnum, ConversationTypeEnum } from "../../modules/shared/model";
import { updateRefundRequest } from "../../redux/Slices/RefundRequestSlice";
import { useWebSocket } from "../../hooks/useWebSocket";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import {
    createMilestonePaymentLog,
    fetchMilestonePaymentLogsByMilestone
} from "../../redux/Slices/contractSlice";
import { MilestonePaymentLogCreateDto } from "../../modules/shared/model";
import RejectionDetailsDialog from "./RejectionDetailsDialog";
import { useNavigate } from "react-router-dom";
import { generateOrderSummaryPDF } from "../../utils/pdfGenerator";
import ProfessionalReviewModal from "./ProfessionalReviewModal";
import { areAllMilestonesReleasedByPaymentLogs, hasUserReviewedMilestone } from "../../utils/reviewUtils";
import { calculateUserAverageRating } from "../../utils/ratingCalculationUtils";
import { checkUserBillingInfo } from "../../utils/billingUtils";
import BillingAddressRequirementDialog from "./BillingAddressRequirementDialog";
import { trackActivity, checkCanCommunicate } from "../../redux/Actions/ProfessionalMonitoringActions";
import ReportProfessionalButton from "../client/ReportProfessionalButton";
import { useTranslation } from "react-i18next";

interface OrderDetailsModalProps {
    order: any;
    onClose: (messageData?: any) => void;
    onRefresh?: (messageData?: any) => void; // Optional callback to refresh data after actions
    hideContactClientButton?: boolean; // Optional prop to hide Contact Client button
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
    order,
    onClose,
    onRefresh,
    hideContactClientButton = false
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const { sendMessage: sendWebSocketMessage } = useWebSocket();
    const { milestonePaymentLogs } = useSelector((state: RootState) => state.contracts);

    // Internal state for tracker checkboxes
    const [trackerCheckboxStates, setTrackerCheckboxStates] = useState<{ [contractId: string]: boolean }>({});
    const [showServicesDialog, setShowServicesDialog] = useState(false);
    const [milestonePaymentLogsMap, setMilestonePaymentLogsMap] = useState<{ [milestoneId: string]: any[] }>({});
    const [isFetchingLogs, setIsFetchingLogs] = useState(false);
    const fetchLogsRef = useRef(false);
    const [showRejectionDialog, setShowRejectionDialog] = useState(false);
    const [selectedRejectionData, setSelectedRejectionData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState<string | null>(null);
    const [contractActionLoading, setContractActionLoading] = useState<string | null>(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedMilestoneForReview, setSelectedMilestoneForReview] = useState<any>(null);
    const [localOrderData, setLocalOrderData] = useState<any>(null);
    const [showRejectRefundDialog, setShowRejectRefundDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [refundLoading, setRefundLoading] = useState(false);
    const [refundUpdateSuccess, setRefundUpdateSuccess] = useState(false);
    const [showFullPageLoader, setShowFullPageLoader] = useState(false);
    const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
    const [selectedContractForBilling, setSelectedContractForBilling] = useState<any>(null);

    // Handle PDF download
    const handleDownloadPDF = () => {
        try {
            generateOrderSummaryPDF(order);
        } catch (error) {
            console.error("Error generating PDF:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.failedToGeneratePDF"));
        }
    };

    // Initialize local order data
    useEffect(() => {
        if (order) {
            setLocalOrderData(order);
        }
    }, [order]);

    // Fetch milestone payment logs when modal opens
    useEffect(() => {
        if (order?.milestones && order.milestones.length > 0 && !fetchLogsRef.current) {
            fetchLogsRef.current = true;
            setIsFetchingLogs(true);

            const fetchPaymentLogs = async () => {
                const logsMap: { [milestoneId: string]: any[] } = {};
                for (const milestone of order.milestones) {
                    try {
                        const result = await dispatch(fetchMilestonePaymentLogsByMilestone(milestone.id)).unwrap();
                        logsMap[milestone.id] = result.data || [];
                    } catch (error) {
                        console.error(`Failed to fetch payment logs for milestone ${milestone.id}:`, error);
                        logsMap[milestone.id] = [];
                    }
                }
                setMilestonePaymentLogsMap(logsMap);
                setIsFetchingLogs(false);
            };
            fetchPaymentLogs();
        }
    }, [order?.id, dispatch]);

    // Reset fetchLogsRef when order changes
    useEffect(() => {
        fetchLogsRef.current = false;
    }, [order?.id]);

    // Cleanup function to prevent memory leaks
    useEffect(() => {
        return () => {
            // Reset states when component unmounts
            setShowRejectRefundDialog(false);
            setRejectReason("");
            setRefundLoading(false);
            setRefundUpdateSuccess(false);
            setIsLoading(false);
            setLoadingAction(null);
        };
    }, []);

    // Helper functions for payment status - memoized to prevent unnecessary recalculations
    const getLatestPaymentStatus = useCallback((milestoneId: string) => {
        const logs = milestonePaymentLogsMap[milestoneId] || [];
        if (logs.length === 0) return null;

        // Create a copy of the array before sorting to avoid mutating the original
        const logsCopy = [...logs];
        const sortedLogs = logsCopy.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        return sortedLogs[0];
    }, [milestonePaymentLogsMap]);

    const getPaymentStatusInfo = useCallback((milestoneId: string) => {
        const latestLog = getLatestPaymentStatus(milestoneId);
        if (!latestLog) {
            return {
                status: null,
                statusId: null,
                reason: null,
                reasonAttachments: [],
                createdAt: null
            };
        }

        return {
            status: latestLog.paymentStatus?.name || "Unknown",
            statusId: latestLog.paymentStatus?.id,
            reason: latestLog.reason,
            reasonAttachments: latestLog.reasonAttachments || [],
            createdAt: latestLog.createdAt
        };
    }, [getLatestPaymentStatus]);

    // Internal handlers using shared utilities
    const handleTrackerCheckboxChange = (contractId: string, checked: boolean) => {
        defaultHandleTrackerCheckboxChange(contractId, checked, setTrackerCheckboxStates);
    };

    // Handle showing rejection details
    const handleShowRejectionDetails = (milestone: any) => {
        const latestPaymentLog = getLatestPaymentStatus(milestone.id);
        if (latestPaymentLog && Number(latestPaymentLog.paymentStatus?.id) === MilestonePaymentStatusEnum.Rejected) {
            setSelectedRejectionData({
                milestone,
                paymentLog: latestPaymentLog
            });
            setShowRejectionDialog(true);
        }
    };

    const handleOpenReviewModal = (milestone: any) => {
        setSelectedMilestoneForReview(milestone);
        setShowReviewModal(true);
    };

    const handleCloseReviewModal = () => {
        setShowReviewModal(false);
        setSelectedMilestoneForReview(null);
    };

    const handleReviewSubmitted = () => {
        // Refresh data after review submission
        if (onRefresh) {
            onRefresh();
        }

        // Update the milestone data locally to include the new review
        // This ensures the Rate Client button is immediately hidden
        if (selectedMilestoneForReview) {
            const newReview = {
                id: Date.now().toString(), // Temporary ID
                author: { id: user?.id },
                user: { id: order.clientInfo?.id || order.clientId },
                rating: 0, // Will be updated when data is refreshed
                createdAt: new Date().toISOString()
            };

            // Update the local order data to include the new review in the milestone
            setLocalOrderData((prevOrder: any) => {
                if (!prevOrder) return prevOrder;

                return {
                    ...prevOrder,
                    milestones: prevOrder.milestones.map((milestone: any) => {
                        if (milestone.id === selectedMilestoneForReview.id) {
                            return {
                                ...milestone,
                                reviews: [...(milestone.reviews || []), newReview]
                            };
                        }
                        return milestone;
                    })
                };
            });

            // Update the selected milestone for review
            setSelectedMilestoneForReview((prevMilestone: any) => ({
                ...prevMilestone,
                reviews: [...(prevMilestone.reviews || []), newReview]
            }));
        }

        // Close the modal after review submission
        setTimeout(() => {
            onClose();
        }, 1000); // Small delay to show the updated state before closing
    };

    // Handle refund request approval
    const handleApproveRefund = async (refundRequest: any) => {
        setRefundLoading(true);
        try {
            await dispatch(updateRefundRequest({
                id: refundRequest.id,
                data: {
                    status: { id: RefundRequestStatusEnum.Approved }
                }
            })).unwrap();

            GlobalService.showToast("success", t("toast.titles.success"), t("orderDetailsModal.refundApprovedSuccess"));

            // Show success feedback
            setRefundUpdateSuccess(true);

            // Update local order data immediately for real-time UI update
            setLocalOrderData((prevOrder: any) => {
                if (!prevOrder || !prevOrder.refundRequest) return prevOrder;

                return {
                    ...prevOrder,
                    refundRequest: {
                        ...prevOrder.refundRequest,
                        status: { id: RefundRequestStatusEnum.Approved }
                    }
                };
            });

            // Refresh data if callback is provided
            if (onRefresh) {
                onRefresh();
            }

            // Close the modal after successful approval
            setTimeout(() => {
                onClose();
            }, 2000); // Increased delay to show the updated status

        } catch (error: any) {
            console.error("Failed to approve refund request:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.refundApproveFailed", { error: error?.message || "Unknown error" }));
        } finally {
            setRefundLoading(false);
        }
    };

    // Handle refund request rejection
    const handleRejectRefund = async (refundRequest: any) => {
        if (!rejectReason.trim()) {
            GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.provideRejectionReason"));
            return;
        }

        setRefundLoading(true);
        try {
            await dispatch(updateRefundRequest({
                id: refundRequest.id,
                data: {
                    status: { id: RefundRequestStatusEnum.Rejected },
                    rejectReason: rejectReason.trim()
                }
            })).unwrap();

            GlobalService.showToast("success", t("toast.titles.success"), t("orderDetailsModal.refundRejectedSuccess"));

            // Show success feedback
            setRefundUpdateSuccess(true);

            // Update local order data immediately for real-time UI update
            setLocalOrderData((prevOrder: any) => {
                if (!prevOrder || !prevOrder.refundRequest) return prevOrder;

                return {
                    ...prevOrder,
                    refundRequest: {
                        ...prevOrder.refundRequest,
                        status: { id: RefundRequestStatusEnum.Rejected },
                        rejectReason: rejectReason.trim()
                    }
                };
            });

            // Close dialog and reset state
            setShowRejectRefundDialog(false);
            setRejectReason("");

            // Refresh data if callback is provided
            if (onRefresh) {
                onRefresh();
            }

            // Close the modal after successful rejection
            setTimeout(() => {
                onClose();
            }, 2000); // Increased delay to show the updated status

        } catch (error: any) {
            console.error("Failed to reject refund request:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.refundRejectFailed", { error: error?.message || "Unknown error" }));
        } finally {
            setRefundLoading(false);
        }
    };

    // Get refund request status info - memoized to prevent unnecessary recalculations
    const getRefundRequestInfo = useCallback((refundRequest: any) => {
        if (!refundRequest) return null;

        const statusId = Number(refundRequest.status?.id);

        switch (statusId) {
            case RefundRequestStatusEnum.Active:
                return {
                    text: "Refund Requested",
                    className: "bg-yellow-100 text-yellow-800",
                    showReason: false
                };
            case RefundRequestStatusEnum.Approved:
                return {
                    text: "Refund Request Approved",
                    className: "bg-green-100 text-green-800",
                    showReason: false
                };
            case RefundRequestStatusEnum.Rejected:
                return {
                    text: "Refund Request Rejected",
                    className: "bg-red-100 text-red-800",
                    showReason: true,
                    reason: refundRequest.rejectReason || "No reason provided"
                };
            default:
                return {
                    text: "Refund Request",
                    className: "bg-gray-100 text-gray-800",
                    showReason: false
                };
        }
    }, []);

    // Handle billing saved callback
    const handleBillingSaved = () => {
        // Check if user now has billing information
        const billingCheck = checkUserBillingInfo(user);
        
        if (billingCheck.hasBillingInfo && selectedContractForBilling) {
            // Proceed with accepting the contract
            handleContractAction(selectedContractForBilling, "accept");
            setSelectedContractForBilling(null);
        }
    };

    const handleContractAction = async (contractId: string, action: "accept" | "decline") => {
        // Check communication restrictions if user is a professional
        const userRoleId = Number(user?.role?.id);
        const isProfessional = userRoleId === 4 || userRoleId === 5;
        
        if (isProfessional) {
            // Get client ID from contract structure - try multiple paths
            const clientId = order?.milestones?.[0]?.escrow?.client?.id || 
                           order?.clientInfo?.id || 
                           order?.client?.id;
            
            if (clientId) {
                try {
                    const commCheck: any = await dispatch(checkCanCommunicate({ 
                        professionalId: user.id, 
                        clientId: clientId 
                    })).unwrap();
                
                if (!commCheck.canCommunicate) {
                    const suspensionEndDate = commCheck.suspensionEndDate 
                        ? new Date(commCheck.suspensionEndDate).toLocaleDateString() 
                        : "unknown date";
                    
                    if (commCheck.suspensionType === "new_clients_only") {
                        GlobalService.showToast(
                            "error", 
                            t("orderDetailsModal.contractActionRestricted"), 
                            t("orderDetailsModal.suspendedNewClientsMessage", { date: suspensionEndDate })
                        );
                    } else {
                        GlobalService.showToast(
                            "error", 
                            t("orderDetailsModal.contractActionRestricted"), 
                            t("orderDetailsModal.suspendedAllClientsMessage", { date: suspensionEndDate })
                        );
                    }
                    return;
                }
                } catch (error) {
                    // Silent error - allow action if check fails
                }
            }
        }

        // Check if user has billing information for accepting contracts
        const billingCheck = checkUserBillingInfo(user);
        
        if (!billingCheck.hasBillingInfo && action === "accept") {
            // Store the contract ID for auto-accepting after billing setup
            setSelectedContractForBilling(contractId);
            // Show billing address requirement dialog
            setShowBillingAddressDialog(true);
            return;
        }

        setContractActionLoading(action);

        try {
            // Find the first milestone from the order
            const firstMilestone = order.milestones?.[0];

            if (!firstMilestone) {
                GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.errorNoMilestone"));
                return;
            }

            // Check if escrow is funded
            const isEscrowFunded = firstMilestone.paymentStatus === "paid";

            if (!isEscrowFunded) {
                GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.errorEscrowMustBeFunded"));
                return;
            }

            // Show confirmation alert for decline action
            if (action === "decline") {
                const confirmed = window.confirm(t("orderDetailsModal.declineContractConfirmation"));

                if (!confirmed) {
                    return; // User cancelled the action
                }
            }

            // Update milestone status
            const milestoneStatus = action === "accept" ? MilestoneStatusEnum.Accepted : MilestoneStatusEnum.Rejected;
            await dispatch(updateMilestone({
                id: firstMilestone.id,
                data: {
                    status: { id: milestoneStatus }
                }
            }));

            const contractStatus = action === "accept" ? ContractStatusEnum.Accepted : ContractStatusEnum.Rejected;
            await dispatch(updateContract({
                id: contractId,
                data: {
                    status: { id: contractStatus }
                }
            }));

            // Track professional activity when contract is accepted
            if (action === "accept") {
                try {
                    const clientId = order?.milestones?.[0]?.escrow?.client?.id || 
                                   order?.clientInfo?.id || 
                                   order?.client?.id;

                    if (user?.id && clientId) {
                        await dispatch(trackActivity({
                            professionalId: user.id.toString(),
                            activityType: "contract_created",
                            contractId: contractId,
                            clientId: clientId.toString(),
                            amount: order?.amount || order?.totalAmount || 0,
                            metadata: {
                                professionalType: "home_service",
                                category: "general"
                            }
                        })).unwrap();
                    }
                } catch (trackError) {
                    console.error("Failed to track contract creation:", trackError);
                    // Silent error handling - don't fail the contract acceptance if tracking fails
                }
            }

            // If declining, refund the escrow
            if (action === "decline") {
                try {
                    await escrowAPI.refundByMilestone(firstMilestone.id);
                    GlobalService.showToast("success", t("toast.titles.success"), t("orderDetailsModal.escrowRefundedSuccess"));
                } catch (error: any) {
                    console.error("Failed to refund escrow:", error);
                    GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.escrowRefundFailed", { error: error?.message || "Unknown error" }));
                }
            }

            // Send conversation message for contract action
            const messageData = await createChatAndSendMessageForContract(contractId, action);
            setTimeout(() => {
                GlobalService.showToast(
                    "success",
                    "Success",
                    action === "accept" ? t("orderDetailsModal.contractAcceptedSuccess") : t("orderDetailsModal.contractDeclinedSuccess")
                );

                // Refresh data if callback is provided
                if (onRefresh) {
                    onRefresh(messageData);
                }

                // Close the modal after successful action
                onClose(messageData);
            }, 500);

        } catch (error: any) {
            GlobalService.showToast("error", "Error", error?.message || t("orderDetailsModal.contractUpdateFailed"));
        } finally {
            setContractActionLoading(null);
        }
    };


    const handleRequestPaymentRelease = async (milestone: any) => {
        setIsLoading(true);
        setLoadingAction(`requesting-payment-${milestone.id}`);
        setShowFullPageLoader(true);

        try {
            // Find the milestone from the order
            if (!milestone) {
                GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.errorNoMilestone"));
                throw new Error("Milestone not found");
            }

            // Check if milestone has escrow data
            if (!milestone.escrow) {
                GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.errorNoEscrow"));
                throw new Error("Escrow not found for this milestone");
            }

            // Create milestone payment log for requested status
            const paymentLogData: MilestonePaymentLogCreateDto = {
                milestone: { id: milestone.id },
                paymentStatus: { id: MilestonePaymentStatusEnum.Requested }
            };

            await dispatch(createMilestonePaymentLog(paymentLogData)).unwrap();

            // Update local state immediately for real-time UI update
            setMilestonePaymentLogsMap(prev => {
                const existingLogs = prev[milestone.id] || [];
                // Check if we already have a log for this milestone to prevent duplicates
                const hasExistingLog = existingLogs.some(log =>
                    Number(log.paymentStatus?.id) === MilestonePaymentStatusEnum.Requested
                );

                if (hasExistingLog) {
                    return prev; // Don't add duplicate
                }

                return {
                    ...prev,
                    [milestone.id]: [
                        ...existingLogs,
                        {
                            id: Date.now().toString(), // Temporary ID until we get the real one
                            milestone: { id: milestone.id },
                            paymentStatus: { id: MilestonePaymentStatusEnum.Requested },
                            createdAt: new Date().toISOString()
                        }
                    ]
                };
            });

            // Force re-render by updating local order data
            setLocalOrderData(prevOrder => {
                if (!prevOrder) return prevOrder;
                return {
                    ...prevOrder,
                    milestones: prevOrder.milestones.map(m => {
                        if (m.id === milestone.id) {
                            return {
                                ...m,
                                paymentStatus: "requested" // Update payment status
                            };
                        }
                        return m;
                    })
                };
            });

            // Use the actual escrow data from the milestone
            const escrow = {
                id: milestone.escrow.id, // Use actual escrow ID
                contract: {
                    id: order.id,
                    title: order.title,
                    description: order.description,
                    amount: order.amount,
                    type: { id: order.isHourly ? 2 : 1 }, // 2 for hourly, 1 for fixed
                    status: { id: order.status },
                    enableTracker: order.enableTracker,
                    weeklyLimit: order.weeklyLimit
                },
                client: order.clientInfo,
                professional: { id: user?.id }, // Current user as professional
                amount: order.amount,
                emergencyCost: order.emergencyCost || 0,
                trackerAmount: order.trackerCost || 0,
                platformFee: order.platformFee || 0,
                totalAmount: order.totalAmount,
                status: { id: order.paymentStatus === "paid" ? 1 : 0 }, // 1 for funded, 0 for pending
                milestone: {
                    id: milestone.id,
                    description: milestone.description,
                    amount: milestone.amount,
                    status: milestone.status,
                    dueDate: milestone.dueDate,
                    paymentStatus: milestone.paymentStatus
                },
                createdAt: order.date,
                updatedAt: order.dueDate
            };
            console.log("messageData escrow", escrow);
            
            // Add timeout to prevent hanging on chat creation
            const chatPromise = createChatAndSendMessage(escrow);
            const chatTimeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error("Chat creation timeout")), 10000)
            );
            
            let messageData = null;
            try {
                messageData = await Promise.race([chatPromise, chatTimeoutPromise]);
                console.log("messageData", messageData);
            } catch (chatError) {
                console.warn("⚠️ Chat creation failed, but continuing with payment release:", chatError);
                // Continue with payment release even if chat creation fails
            }

            setTimeout(() => {
                GlobalService.showToast("success", t("toast.titles.success"), t("orderDetailsModal.paymentReleasedSuccess"));
                // Hide full page loader before closing modal
                setShowFullPageLoader(false);
                // Close the modal after successful action
                onClose(messageData);
                // Refresh data if callback is provided
                if (onRefresh) {
                    onRefresh(messageData);
                }


            }, 500);

        } catch (error: any) {
            console.error("Failed to request payment release:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("orderDetailsModal.paymentReleaseFailed", { error: error?.message || "Unknown error" }));
            // Hide full page loader on error
            setShowFullPageLoader(false);
        } finally {
            setIsLoading(false);
            setLoadingAction(null);
        }
    };

    // Function to create chat and send contract action message
    const createChatAndSendMessageForContract = async (contractId: string, action: "accept" | "decline") => {
        try {
            // Get client and professional IDs from order
            const escrow = order?.milestones?.[0].escrow;
            const clientId = escrow.client.id;
            const professionalId = escrow.professional.id;

            if (!clientId || !professionalId) {
                console.warn("Missing client or professional ID in order, skipping conversation message");
                return null;
            }

            // Check if chat already exists between client and professional
            let existingChat = null;
            try {
                existingChat = await dispatch(fetchParticipantChats({ currentUserId: clientId, page: 1, limit: 50 })).unwrap();
            } catch (error) {
                // No existing chat found, will create new one
            }

            let chatResponse;
            if (existingChat.chats && existingChat.chats.length > 0) {
                const filteredChats = existingChat.chats.filter((chat: any) => {
                    const participantIds = chat.chat.participants.map((p: any) => p.id);
                    return participantIds.includes(clientId) && participantIds.includes(professionalId);
                });
                const chat = filteredChats[0].chat;
                chatResponse = chat;

                // Update the chat with lastMessage and lastMessageAt
                if (chatResponse && chatResponse.id) {
                    const message = action === "accept" ? "Contract has been accepted" : "Contract has been rejected";
                    const updatedChat = {
                        lastMessage: message,
                        lastMessageAt: new Date().toISOString(),
                        unreadCount: { [clientId]: user?.id === professionalId ? 1 : 0, [professionalId]: user?.id === clientId ? 1 : 0 },
                    };
                    await dispatch(updateChat({
                        chatId: chatResponse.id,
                        data: updatedChat
                    }));
                }
            } else {
                // Create new chat
                const message = action === "accept" ? "Contract has been accepted" : "Contract has been rejected";
                const chatPayload = {
                    unreadCount: { [clientId]: user?.id === professionalId ? 0 : 1, [professionalId]: user?.id === clientId ? 0 : 1 },
                    participants: [
                        { id: clientId },
                        { id: professionalId }
                    ],
                    contextId: contractId,
                    contextType: { id: ContextEnum.Message },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: message,
                };

                chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
            }

            // Send conversation message
            const message = action === "accept" ? "Contract has been accepted" : "Contract has been rejected";
            const conversationType = action === "accept" ? ConversationTypeEnum.Contract_Accepted : ConversationTypeEnum.Contract_Rejected;

            const conversationPayload = {
                isRead: false,
                message: message,
                sender: { id: user?.id },
                chatId: { id: chatResponse.id },
                type: { id: conversationType },
                contextId: { id: escrow.id },
                attachments: []
            };

            const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();

            // Send WebSocket message to notify other participants (non-blocking)
            const messageData = {
                type: "new_conversation",
                detail: {
                    conversationId: conversation.id,
                    conversation: conversation,
                    chatId: conversation.chatId.id,
                    message: conversationPayload.message,
                    senderId: conversation.sender.id,
                    attachments: conversationPayload.attachments,
                    type: conversationPayload.type.id,
                    referenceId: conversationPayload.contextId.id,
                    timestamp: new Date().toISOString()
                }
            };

            setTimeout(async () => {
                try {
                    if (sendWebSocketMessage) {
                        const receivers = chatResponse.participants.map((p: any) => p.id).filter((id: any) => id !== user?.id);

                        if (receivers.length > 0) {
                            await sendWebSocketMessage({
                                receivers,
                                sender: user?.id || "",
                                type: "new_conversation",
                                detail: messageData.detail,
                                conversationId: conversationPayload.chatId.id
                            });
                        }
                    }
                } catch (wsError) {
                    // Don't fail the entire operation if WebSocket fails
                }
            }, 100);

            return messageData;
        } catch (error) {
            console.error("Error creating chat or sending message:", error);
            return null;
        }
    };

    // Function to create chat and send payment release message
    const createChatAndSendMessage = async (escrow: any) => {
        try {
            // Check if chat already exists between client and professional
            let existingChat = null;
            try {
                existingChat = await dispatch(fetchParticipantChats({ currentUserId: escrow.client.id, page: 1, limit: 50 })).unwrap();
            } catch (error) {
                // No existing chat found, will create new one
            }

            let chatResponse;
            if (existingChat.chats && existingChat.chats.length > 0) {
                const filteredChats = existingChat.chats.filter((chat: any) => {
                    const participantIds = chat.chat.participants.map((p: any) => p.id);
                    return participantIds.includes(escrow.client.id) && participantIds.includes(escrow.professional.id);
                });
                const chat = filteredChats[0].chat;
                chatResponse = chat;

                // Update the chat with lastMessage and lastMessageAt
                if (chatResponse && chatResponse.id) {
                    const updatedChat = {
                        lastMessage: t("myOrdersPage.chat.paymentReleaseRequest"),
                        lastMessageAt: new Date().toISOString(),
                        unreadCount: { [escrow.client?.id]: 1, [escrow.professional?.id]: 0 },
                    };
                    await dispatch(updateChat({
                        chatId: chatResponse.id,
                        data: updatedChat
                    }));
                }
            } else {
                // Create new chat
                const chatPayload = {
                    unreadCount: { [escrow.client?.id]: 0, [escrow.professional?.id]: 1 },
                    participants: [
                        { id: escrow.client?.id },
                        { id: escrow.professional?.id }
                    ],
                    contextId: escrow?.contract?.id,
                    contextType: { id: ContextEnum.Message },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: t("myOrdersPage.chat.paymentReleaseRequest"),
                };

                chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
            }

            // Send payment release message with proper escrow contextId
            const conversationPayload = {
                isRead: false,
                message: t("myOrdersPage.chat.paymentReleaseRequested"),
                sender: { id: user.id },
                chatId: { id: chatResponse.id },
                type: { id: ConversationTypeEnum.Payment_Request },
                contextId: { id: escrow.id }, // This will now be the actual escrow ID
                attachments: [],
            };

            const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();

            // Send WebSocket message to notify other participants
            const messageData = {
                type: "new_conversation",
                detail: {
                    conversationId: conversation.id,
                    conversation: conversation,
                    chatId: conversation.chatId.id,
                    message: conversationPayload.message,
                    senderId: conversation.sender.id,
                    attachments: conversationPayload.attachments,
                    type: conversationPayload.type.id,
                    referenceId: conversationPayload.contextId.id,
                    timestamp: new Date().toISOString()
                }
            };

            try {
                if (sendWebSocketMessage) {
                    const receivers = chatResponse.participants.map(p => p.id).filter(id => id !== user?.id);

                    if (receivers.length > 0) {
                        // Add timeout to prevent hanging
                        const wsPromise = sendWebSocketMessage({
                            receivers,
                            sender: user?.id || "",
                            type: "new_conversation",
                            detail: messageData.detail,
                            conversationId: conversationPayload.chatId.id
                        });
                        
                        // Set a 5-second timeout for WebSocket message
                        const timeoutPromise = new Promise((_, reject) => 
                            setTimeout(() => reject(new Error("WebSocket timeout")), 5000)
                        );
                        
                        await Promise.race([wsPromise, timeoutPromise]);
                    }
                }
            } catch (wsError) {
                console.warn("⚠️ Failed to send WebSocket message for payment release:", wsError);
                // Don't fail the entire operation if WebSocket fails
            }

            return messageData;
        } catch (error) {
            console.error("Error creating chat or sending message:", error);
            // Don't throw error here as the main action (payment release request) should still proceed
            return null;
        }
    };
    if (!order) return null;

    // Loading spinner component
    const LoadingSpinner = () => (
        <Loader2 className="h-4 w-4 animate-spin" />
    );

    // Full page loading component - similar to client.tsx
    const FullPageLoadingSpinner = () => (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-60 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-lg flex items-center space-x-3">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-gray-700 font-medium">{t("orderDetailsModal.processingPaymentRelease")}</span>
            </div>
        </div>
    );

    // Status badge component
    const StatusBadge = ({ status }: { status: number }) => {
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getContractStatusColor(status)}`}>
                {getContractStatusText(status)}
            </span>
        );
    };

    // Star rating display component with decimal support
    const StarRatingDisplay = ({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" | "lg" }) => {
        const sizeClasses = {
            sm: "h-4 w-4",
            md: "h-5 w-5",
            lg: "h-6 w-6"
        };

        return (
            <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => {
                    const isHalfFilled = rating >= star - 0.5 && rating < star;
                    const isFilled = rating >= star;

                    return (
                        <div key={star} className="relative">
                            <Star
                                className={`${sizeClasses[size]} ${isFilled
                                        ? "fill-yellow-400 text-yellow-400"
                                        : "text-gray-300"
                                    }`}
                            />
                            {isHalfFilled && (
                                <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                                    <Star
                                        className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Contract type badge component
    const ContractTypeBadge = ({ contractType, isHourly, weeklyLimit }: { contractType: string; isHourly: boolean; weeklyLimit: number }) => {
        if (isHourly) {
            return (
                <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                        {t("orderDetailsModalComplete.hourly")}
                    </span>
                    <span className="text-xs text-gray-500">
                        {weeklyLimit}h/week
                    </span>
                </div>
            );
        }
        return (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {t("orderDetailsModalComplete.fixedPrice")}
            </span>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-4 md:p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-6">
                    <div className="pr-8">
                        <h2 className="text-xl font-bold text-gray-900">{(localOrderData || order).title}</h2>
                        <p className="text-sm text-gray-600">{t("orderDetailsModalComplete.orderId")}: {(localOrderData || order).id}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
                    >
                        <XCircle className="h-6 w-6 text-gray-500" />
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t("orderDetailsModalComplete.orderDetails")}</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.status")}</span>
                                <StatusBadge status={(localOrderData || order).status} />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.contractType")}</span>
                                <ContractTypeBadge
                                    contractType={(localOrderData || order).contractType}
                                    isHourly={(localOrderData || order).isHourly}
                                    weeklyLimit={(localOrderData || order).weeklyLimit}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.baseAmount")}</span>
                                <span className="font-medium">
                                    {(localOrderData || order).isHourly ? `${getDynamicCurrencySymbol()}${(localOrderData || order).amount.toFixed(2)}/hour` : `${getDynamicCurrencySymbol()}${(localOrderData || order).amount.toFixed(2)}`}
                                </span>
                            </div>
                            {(localOrderData || order).isHourly && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t("orderDetailsModalComplete.weeklyAmount")}</span>
                                    <span className="font-medium">{getDynamicCurrencySymbol()}{(localOrderData || order).weeklyAmount.toFixed(2)}</span>
                                </div>
                            )}
                            {(localOrderData || order).isEmergency && (localOrderData || order).emergencyCost > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-red-600">{t("orderDetailsModalComplete.emergencyCost")}</span>
                                    <span className="font-medium text-red-600">+{getDynamicCurrencySymbol()}{(localOrderData || order).emergencyCost.toFixed(2)}</span>
                                </div>
                            )}
                            {(localOrderData || order).trackerCost > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-blue-600">{t("orderDetailsModalComplete.smartWorkTracker")}</span>
                                    <span className="font-medium text-blue-600">{t("orderDetailsModalComplete.enabled")}</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between border-t pt-2">
                                <span className="text-gray-600 font-semibold">{t("orderDetailsModalComplete.totalAmount")}</span>
                                <span className="font-bold text-lg">{getDynamicCurrencySymbol()}{((localOrderData || order).totalAmount + ((localOrderData || order).emergencyCost || 0)).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.dueDate")}</span>
                                <span>{new Date((localOrderData || order).dueDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.location")}</span>
                                <span>{(localOrderData || order).location}</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t("orderDetailsModalComplete.clientInformation")}</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.clientName")}</span>
                                <span>{(localOrderData || order).client}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.rating")}</span>
                                <div className="flex items-center gap-1">
                                    <StarRatingDisplay
                                        rating={(() => {
                                            const clientId = (localOrderData || order).clientInfo?.id;
                                            if (!clientId) return 0;

                                            // Extract all reviews from all milestones in this order
                                            const allOrderReviews = (localOrderData || order).milestones?.flatMap((milestone: any) => milestone.reviews || []) || [];
                                            const clientRatingData = calculateUserAverageRating(allOrderReviews, user.id);
                                            return clientRatingData.average || 0;
                                        })()}
                                        size="sm"
                                    />
                                    <span>
                                        {(() => {
                                            const clientId = (localOrderData || order).clientInfo?.id;
                                            if (!clientId) return "0.0";

                                            // Extract all reviews from all milestones in this order
                                            const allOrderReviews = (localOrderData || order).milestones?.flatMap((milestone: any) => milestone.reviews || []) || [];
                                            const clientRatingData = calculateUserAverageRating(allOrderReviews, user.id);
                                            return clientRatingData.average ? clientRatingData.average.toFixed(1) : "0.0";
                                        })()}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.totalJobs")}</span>
                                <span>{(localOrderData || order).clientInfo?.completedJobs || (localOrderData || order).clientInfo?.totalJobs || t("orderDetailsModalComplete.notSpecified")} {t("orderDetailsModalComplete.jobs")}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.location")}</span>
                                <span>{(localOrderData || order).clientInfo?.location || (localOrderData || order).clientInfo?.address || t("orderDetailsModalComplete.notSpecified")}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.messages")}</span>
                                <span className="text-blue-600">{t("orderDetailsModalComplete.messagesCount", { count: (localOrderData || order).messages })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.attachments")}</span>
                                <span className="flex items-center gap-1">
                                    <FileText className="h-4 w-4 text-gray-400" />
                                    {(localOrderData || order).attachments} {t("orderDetailsModalComplete.files")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contract Files Section */}
                {((localOrderData || order).files && (localOrderData || order).files.length > 0) && (
                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t("orderDetailsModalComplete.contractFiles")}</h3>
                        <div className="space-y-2">
                            {(localOrderData || order).files.map((file: any, index: number) => (
                                <div key={file.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <Paperclip className="h-5 w-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {file.path?.split("/").pop() || `${t("orderDetailsModalComplete.file")} ${index + 1}`}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {t("orderDetailsModalComplete.contractDocument")}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => window.open(file.path, "_blank")}
                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title={t("orderDetailsModalComplete.viewFile")}
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => {
                                                const link = document.createElement("a");
                                                link.href = file.path;
                                                link.download = file.path?.split("/").pop() || `contract-file-${index + 1}`;
                                                link.click();
                                            }}
                                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                            title={t("orderDetailsModalComplete.downloadFile")}
                                        >
                                            <Download className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Contract Summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">{t("orderDetailsModalComplete.contractSummary")}</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t("orderDetailsModalComplete.contractType")}:</span>
                            <ContractTypeBadge
                                contractType={(localOrderData || order).contractType}
                                isHourly={(localOrderData || order).isHourly}
                                weeklyLimit={(localOrderData || order).weeklyLimit}
                            />
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t("orderDetailsModalComplete.baseAmount")}:</span>
                            <span className="font-medium">
                                {(localOrderData || order).isHourly ? `${getDynamicCurrencySymbol()}${(localOrderData || order).amount.toFixed(2)}/hour` : `${getDynamicCurrencySymbol()}${(localOrderData || order).amount.toFixed(2)}`}
                            </span>
                        </div>
                        {(localOrderData || order).isHourly && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.weeklyHours")}:</span>
                                <span className="font-medium">{(localOrderData || order).weeklyLimit} {t("orderDetailsModalComplete.hours")}</span>
                            </div>
                        )}
                        {(localOrderData || order).isHourly && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">{t("orderDetailsModalComplete.weeklyAmount")}:</span>
                                <span className="font-medium">{getDynamicCurrencySymbol()}{(localOrderData || order).weeklyAmount.toFixed(2)}</span>
                            </div>
                        )}
                        {(localOrderData || order).isEmergency && (localOrderData || order).emergencyCost > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-red-600">{t("orderDetailsModalComplete.emergencyCost")}:</span>
                                <span className="font-medium text-red-600">+{getDynamicCurrencySymbol()}{(localOrderData || order).emergencyCost.toFixed(2)}</span>
                            </div>
                        )}
                        {(localOrderData || order).trackerCost > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-600">{t("orderDetailsModalComplete.smartWorkTracker")}:</span>
                                <span className="font-medium text-blue-600">{t("orderDetailsModalComplete.enabled")}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold border-t pt-2">
                            <span>{t("orderDetailsModalComplete.totalContractValue")}:</span>
                            <span className="text-lg">{getDynamicCurrencySymbol()}{((localOrderData || order).totalAmount + ((localOrderData || order).emergencyCost || 0)).toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Refund Request Section */}
                {(localOrderData || order).refundRequest && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">{t("orderDetailsModalComplete.refundRequest")}</h3>
                        {(() => {
                            const refundRequest = (localOrderData || order).refundRequest;
                            const refundInfo = getRefundRequestInfo(refundRequest);

                            return (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">{t("orderDetailsModalComplete.refundRequestStatus")}:</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${refundInfo?.className}`}>
                                                {refundInfo?.text}
                                            </span>
                                            {refundUpdateSuccess && (
                                                <CheckCircle className="h-4 w-4 text-green-500 animate-pulse" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">{t("orderDetailsModalComplete.refundAmount")}:</span>
                                        <span className="font-medium text-red-600">{getDynamicCurrencySymbol()}{refundRequest.amount?.toFixed(2) || "0.00"}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">{t("orderDetailsModalComplete.refundType")}:</span>
                                        <span className="text-sm">{refundRequest.type?.name || "N/A"}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">{t("orderDetailsModalComplete.refundReason")}:</span>
                                        <span className="text-sm">{refundRequest.reason || t("orderDetailsModalComplete.noReasonProvided")}</span>
                                    </div>
                                    {refundInfo?.showReason && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">{t("orderDetailsModalComplete.rejectionReason")}:</span>
                                            <span className="text-sm text-red-600">{refundInfo.reason}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">{t("orderDetailsModalComplete.requestedDate")}:</span>
                                        <span className="text-sm">{new Date(refundRequest.createdAt).toLocaleDateString()}</span>
                                    </div>

                                    {/* Action buttons for Active refund requests */}
                                    {Number(refundRequest.status?.id) === RefundRequestStatusEnum.Active && (
                                        <div className="flex gap-3 pt-3 border-t">
                                            <button
                                                onClick={() => handleApproveRefund(refundRequest)}
                                                disabled={refundLoading}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {refundLoading && <LoadingSpinner />}
                                                {refundLoading ? t("orderDetailsModalComplete.processing") : t("orderDetailsModalComplete.approveRefund")}
                                            </button>
                                            <button
                                                onClick={() => setShowRejectRefundDialog(true)}
                                                disabled={refundLoading}
                                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {refundLoading && <LoadingSpinner />}
                                                {refundLoading ? t("orderDetailsModalComplete.processing") : t("orderDetailsModalComplete.rejectRefund")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                )}

                <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">{t("orderDetailsModalComplete.contractMilestones")}</h3>
                    <div className="space-y-4">
                        {(localOrderData?.milestones || order.milestones) && (localOrderData?.milestones || order.milestones).length > 0 ? (
                            (localOrderData?.milestones || order.milestones).map((milestone: any, index: number) => (
                                <div key={milestone.id || index} className="border rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                                                {t("orderDetailsModalComplete.milestoneNumber", { number: index + 1 })}
                                            </h4>
                                            <p className="text-sm text-gray-600 mb-2">{milestone.description}</p>
                                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                                <span>{t("orderDetailsModalComplete.amount")}: {getDynamicCurrencySymbol()}{milestone.amount}</span>
                                                {milestone.trackerAmount && Number(milestone.trackerAmount) > 0 && (
                                                    <span className="text-blue-600">{t("orderDetailsModalComplete.tracker")}: {t("orderDetailsModalComplete.enabled")}</span>
                                                )}
                                                {milestone.emergencyCost && Number(milestone.emergencyCost) > 0 && (
                                                    <span className="text-red-600">{t("orderDetailsModalComplete.emergency")}: +{getDynamicCurrencySymbol()}{Number(milestone.emergencyCost).toFixed(2)}</span>
                                                )}
                                                {milestone.dueDate && (
                                                    <span>{t("orderDetailsModalComplete.due")}: {new Date(milestone.dueDate).toLocaleDateString()}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Reviews from other milestones */}
                                        {(() => {

                                            const clientRatingData = calculateUserAverageRating(milestone.reviews, user?.id || "");
                                            return (
                                                <div className="flex flex-col items-end text-right">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <StarRatingDisplay rating={clientRatingData.average} size="sm" />
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {clientRatingData.average ? clientRatingData.average.toFixed(1) : "0.0"}
                                                        </span>
                                                    </div>
                                                </div>
                                            );

                                            return null;
                                        })()}
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-600">{t("orderDetailsModalComplete.milestoneStatus")}:</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMilestoneStatusColor(milestone.status?.id)}`}>
                                                    {getMilestoneStatusText(milestone.status?.id)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-600">{t("orderDetailsModalComplete.paymentStatus")}:</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(milestone.paymentStatus || "no_payment")}`}>
                                                    {getPaymentStatusText(milestone.paymentStatus || "no_payment")}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Payment Status and Actions */}
                                        {(() => {
                                            // Get the latest payment log for this specific milestone
                                            const latestPaymentLog = getLatestPaymentStatus(milestone.id);
                                            
                                            // Get contract status
                                            const contractStatus = Number((localOrderData || order).status);
                                            const isContractCompletedOrClosed = contractStatus === ContractStatusEnum.Completed || contractStatus === ContractStatusEnum.Closed;

                                            // If rejected, show rejected status with reason (regardless of milestone status)
                                            if (latestPaymentLog && Number(latestPaymentLog.paymentStatus?.id) === MilestonePaymentStatusEnum.Rejected) {
                                                return (
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-lg">
                                                            {t("orderDetailsModalComplete.rejected")}
                                                        </span>
                                                        <button
                                                            onClick={() => handleShowRejectionDetails(milestone)}
                                                            className="px-3 py-1 bg-gray-600 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
                                                        >
                                                            {t("orderDetailsModalComplete.viewDetails")}
                                                        </button>
                                                        {/* Hide Request Again button if contract is completed or closed */}
                                                        {!isContractCompletedOrClosed && (
                                                            <button
                                                                onClick={() => handleRequestPaymentRelease(milestone)}
                                                                disabled={isLoading}
                                                                className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                            >
                                                                {loadingAction === `requesting-payment-${milestone.id}` && <LoadingSpinner />}
                                                                {loadingAction === `requesting-payment-${milestone.id}` ? t("orderDetailsModalComplete.requesting") : t("orderDetailsModalComplete.requestAgain")}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            // Only show other actions if milestone is accepted
                                            if (Number(milestone.status?.id) === MilestoneStatusEnum.Accepted) {
                                                // Check if there's a payment request made
                                                if (latestPaymentLog && Number(latestPaymentLog.paymentStatus?.id) === MilestonePaymentStatusEnum.Requested) {
                                                    return (
                                                        <button
                                                            onClick={() => { }}
                                                            className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                                        >
                                                            {t("orderDetailsModalComplete.paymentRequestMade")}
                                                        </button>
                                                    );
                                                }

                                                // If funded, show Request Payment Release button only if contract is not completed or closed
                                                if (latestPaymentLog && Number(latestPaymentLog.paymentStatus?.id) === MilestonePaymentStatusEnum.Funded) {
                                                    if (isContractCompletedOrClosed) {
                                                        return null; // Don't show button if contract is completed or closed
                                                    }
                                                    return (
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleRequestPaymentRelease(milestone)}
                                                                disabled={isLoading}
                                                                className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                            >
                                                                {loadingAction === `requesting-payment-${milestone.id}` && <LoadingSpinner />}
                                                                {loadingAction === `requesting-payment-${milestone.id}` ? t("orderDetailsModalComplete.requesting") : t("orderDetailsModalComplete.requestReleasePayment")}
                                                            </button>
                                                        </div>
                                                    );
                                                }

                                                // If released, show Payment Released and Review button (only if user hasn't already reviewed)
                                                if (latestPaymentLog && Number(latestPaymentLog.paymentStatus?.id) === MilestonePaymentStatusEnum.Released) {
                                                    return (
                                                        <div className="flex items-center gap-2">
                                                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-lg">
                                                                {t("orderDetailsModalComplete.paymentReleased")}
                                                            </span>
                                                            {!hasUserReviewedMilestone(milestone, user?.id || "") && (
                                                                <button
                                                                    onClick={() => handleOpenReviewModal(milestone)}
                                                                    className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                                                                >
                                                                    <Star className="h-3 w-3" />
                                                                    {t("orderDetailsModalComplete.rateClient")}
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Default case - show Request Payment Release button only if contract is not completed or closed
                                                if (isContractCompletedOrClosed) {
                                                    return null; // Don't show button if contract is completed or closed
                                                }
                                                
                                                return (
                                                    <button
                                                        onClick={() => handleRequestPaymentRelease(milestone)}
                                                        disabled={isLoading}
                                                        className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                    >
                                                        {loadingAction === `requesting-payment-${milestone.id}` && <LoadingSpinner />}
                                                        {loadingAction === `requesting-payment-${milestone.id}` ? t("orderDetailsModalComplete.requesting") : t("orderDetailsModalComplete.requestReleasePayment")}
                                                    </button>
                                                );
                                            }

                                            // If milestone is not accepted and no rejected payment, show no action
                                            return null;
                                        })()}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Clock className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">{t("orderDetailsModalComplete.noMilestonesFound")}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 w-full sm:w-auto flex items-center justify-center gap-2"
                    >
                        <X className="h-4 w-4" />
                        {t("orderDetailsModalComplete.close")}
                    </button>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleDownloadPDF}
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50 w-full sm:w-auto flex items-center justify-center gap-2"
                        >
                            <Download className="h-4 w-4" />
                            {t("orderDetailsModalComplete.downloadDetailsPDF")}
                        </button>
                        {!hideContactClientButton && (
                            <button onClick={() => {
                                window.location.href = "/MessageAndVideoCall";
                                // navigate("/MessageAndVideoCall")
                            }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto">
                                {t("orderDetailsModalComplete.contactClient")}
                            </button>
                        )}
                        {/* Report Professional Button - Only show for clients */}
                        {Number(user?.role?.id) === 2 && (localOrderData || order)?.professional && (
                            <ReportProfessionalButton
                                professional={(localOrderData || order).professional}
                                contractId={(localOrderData || order).id}
                                jobId={(localOrderData || order).job?.id}
                                size="sm"
                            />
                        )}
                    </div>
                </div>

                {/* Accept/Decline Contract Buttons in Modal */}
                {canAcceptDeclineContract(localOrderData || order) && (
                    <div className="mt-6 pt-6 border-t">
                        {/* Smart Work Tracker Checkbox for Hourly Contracts */}
                        {requiresTrackerCheckbox(localOrderData || order) && (
                            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        id={`tracker-checkbox-${(localOrderData || order).id}`}
                                        checked={trackerCheckboxStates[(localOrderData || order).id] || false}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleTrackerCheckboxChange((localOrderData || order).id, e.target.checked);
                                        }}
                                        className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <label
                                                htmlFor={`tracker-checkbox-${(localOrderData || order).id}`}
                                                className="text-sm font-medium text-gray-900 cursor-pointer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {t("orderDetailsModalComplete.smartWorkTrackerAgreement")}
                                            </label>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowServicesDialog(true);
                                                }}
                                                className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                            >
                                                {t("orderDetailsModalComplete.seeServices")}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-1">
                                            {t("orderDetailsModalComplete.smartWorkTrackerDesc")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row justify-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleContractAction((localOrderData || order).id, "accept");
                                }}
                                disabled={!!contractActionLoading || (requiresTrackerCheckbox(localOrderData || order) && !trackerCheckboxStates[(localOrderData || order).id])}
                                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-colors w-full sm:w-auto ${(contractActionLoading || (requiresTrackerCheckbox(localOrderData || order) && !trackerCheckboxStates[(localOrderData || order).id]))
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-green-600 text-white hover:bg-green-700"
                                    }`}
                            >
                                {contractActionLoading === "accept" && <LoadingSpinner />}
                                {contractActionLoading === "accept" ? (
                                    <span>{t("orderDetailsModalComplete.accepting")}</span>
                                ) : (
                                    <>
                                        <CheckCircle className="h-5 w-5" />
                                        <span>{t("orderDetailsModalComplete.acceptContract")}</span>
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    handleContractAction((localOrderData || order).id, "decline");
                                }}
                                disabled={!!contractActionLoading}
                                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-colors w-full sm:w-auto ${contractActionLoading
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-red-600 text-white hover:bg-red-700"
                                    }`}
                            >
                                {contractActionLoading === "decline" && <LoadingSpinner />}
                                {contractActionLoading === "decline" ? (
                                    <span>{t("orderDetailsModalComplete.declining")}</span>
                                ) : (
                                    <>
                                        <XCircle className="h-5 w-5" />
                                        <span>{t("orderDetailsModalComplete.declineContract")}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>

            {/* Rejection Details Dialog */}
            <RejectionDetailsDialog
                isOpen={showRejectionDialog}
                onClose={() => setShowRejectionDialog(false)}
                rejectionData={selectedRejectionData}
                onRequestAgain={handleRequestPaymentRelease}
            />

            {/* Professional Review Modal */}
            {selectedMilestoneForReview && (
                <ProfessionalReviewModal
                    isOpen={showReviewModal}
                    onClose={handleCloseReviewModal}
                    milestoneId={selectedMilestoneForReview.id}
                    clientId={(localOrderData || order).clientInfo?.id || (localOrderData || order).clientId || ""}
                    contractId={(localOrderData || order).id}
                    onReviewSubmitted={handleReviewSubmitted}
                    milestone={selectedMilestoneForReview}
                />
            )}

            {/* Reject Refund Dialog */}
            <AnimatePresence>
                {showRejectRefundDialog && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center p-4"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) setShowRejectRefundDialog(false);
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-xl w-full max-w-md p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">{t("orderDetailsModalComplete.rejectRefundRequestTitle")}</h3>
                                <button
                                    onClick={() => setShowRejectRefundDialog(false)}
                                    className="p-1 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="h-5 w-5 text-gray-500" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("orderDetailsModalComplete.reasonForRejection")}
                                    </label>
                                    <textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder={t("orderDetailsModalComplete.reasonForRejectionPlaceholder")}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        rows={4}
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => setShowRejectRefundDialog(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {t("orderDetailsModalComplete.cancel")}
                                    </button>
                                    <button
                                        onClick={() => handleRejectRefund((localOrderData || order).refundRequest)}
                                        disabled={!rejectReason.trim() || refundLoading}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {refundLoading && <LoadingSpinner />}
                                        {t("orderDetailsModalComplete.rejectRefundButton")}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Full Page Loading Spinner */}
            {showFullPageLoader && <FullPageLoadingSpinner />}

            {/* Billing Address Requirement Dialog */}
            <BillingAddressRequirementDialog
                isOpen={showBillingAddressDialog}
                onClose={() => {
                    setShowBillingAddressDialog(false);
                    setSelectedContractForBilling(null);
                }}
                onBillingSaved={handleBillingSaved}
                userRole={Number(user?.role?.id)}
                action="payment"
            />
        </motion.div>
    );
};

export default OrderDetailsModal;
